import * as React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './View/Style/tailwind.css'
import reportWebVitals from './reportWebVitals.ts'
import * as Sentry from '@sentry/react'
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom'
import { Environment } from './environment.ts'
import { enablePatches } from 'immer'
import type { ErrorEvent } from '@sentry/react'
import { trackAppBoot } from './Services/Telemetry/index.ts'
import { reloadOnStaleChunk } from './Services/reloadOnStaleChunk.ts'
import { replayIntegrationAdded } from './Services/Telemetry/Replay.ts'

reloadOnStaleChunk()

Sentry.init({
  dsn: Environment.SENTRY_DSN,
  tunnel: Environment.SENTRY_TUNNEL,
  environment: Environment.SENTRY_ENVIRONMENT,
  release: Environment.SENTRY_RELEASE_VERSION,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.feedbackIntegration({
      // Additional SDK configuration goes in here, for example:
      colorScheme: 'system',
      autoInject: false
    }),
    Sentry.captureConsoleIntegration({
      levels: ['error', 'warn']
    })
  ],
  beforeSend: (event: ErrorEvent) => {
    /* token_info is often the endpoint triggering the 401 if the user is not authenticated */
    const requestUrl = event?.request?.url || ''
    if (requestUrl.includes('/token_info')) {
      return null
    }
    /* Drop a noisy, non-fatal echarts hover race (SPCFY-FRONTEND-GZ): a mousemove tooltip lookup
     * hits a series whose data was replaced mid-render → "getRawIndex" of undefined. It's a
     * third-party (echarts/zrender) quirk in the viz workspace, caught by Sentry's browser-api
     * instrumentation; the chart keeps working, so it's pure noise. */
    if ((event.exception?.values ?? []).some((value) => value.value?.includes('getRawIndex'))) {
      return null
    }
    /* Drop browser-extension noise (SPCFY-FRONTEND-J3): MetaMask's injected content script logs
     * "ObjectMultiplex - orphaned data for stream 'metamask-multichain-provider'" to the page
     * console, which captureConsoleIntegration then reports. It's not our code and affects no user
     * flow (0 users impacted). */
    if ((event.exception?.values ?? []).some((value) => value.value?.includes('ObjectMultiplex') || value.value?.includes('metamask'))) {
      return null
    }
    return event
  },
  // v11 collects user info, cookies, headers, bodies and query params by default; this is customer data,
  // so keep the pre-v11 behaviour. setUser() calls stay explicit.
  dataCollection: {
    userInfo: false,
    cookies: false,
    httpHeaders: false,
    httpBodies: [],
    urlQueryParams: false
  },
  // Performance Monitoring
  tracesSampleRate: 0.5,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', '.*spcfy\\.io(\\/.*|$)'],
  // Session Replay
  replaysSessionSampleRate: 0.5,
  replaysOnErrorSampleRate: 1.0
})
enablePatches()
trackAppBoot()

import('@sentry/react').then((lazyLoadedSentry) => {
  Sentry.addIntegration(
    lazyLoadedSentry.replayIntegration({
      beforeErrorSampling: (event: ErrorEvent) => {
        /* skip 401 errors */
        return !(event.exception?.values ?? []).some((exception) => exception.type === 'AjaxError' && exception.value?.includes('401'))
      }
    })
  )
  replayIntegrationAdded()
  // Sentry.addIntegration(lazyLoadedSentry.browserTracingIntegration())
})

const root = ReactDOM.createRoot(document.getElementById('root')!, {
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack)
  }),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler()
})

root.render(<App />)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(console.log)
